import { useAuth } from '@clerk/nextjs';
import {
  QueryFunctionContext,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';

import { useLocale } from 'hooks/useLocale';
import { datoRequester } from 'queries/requesters';
import { StaleTime } from 'queries/types';
import { EnhancedTicketsEventCard } from 'restful/accounts/types';
import { TICKETS_PREFIX } from 'restful/tickets/constants';
import { restCall } from 'services/api/restful/client';
import { RestApiError } from 'services/api/restful/errors';
import { getAccountEventsOverview } from 'services/api/restful/generated/accounts/accounts';
import type { AccountEventsOverview } from 'services/api/restful/generated/nofomoRestApi.schemas';
import { SiteLocale } from 'types/api/dato.types';
import { PageEventsCardsDocument } from 'types/api/dato-operations.types';

import { isPastEvent, isUpcomingEvent } from 'components/@events/utils';

type AccountTicketsOverviewData = {
  upcomingEvents: Array<EnhancedTicketsEventCard>;
  pastEvents: Array<EnhancedTicketsEventCard>;
};

const enrichAccountTicketsOverviewWithDatoCards = async (
  accountEventsOverview: AccountEventsOverview,
  locale: SiteLocale,
  ctx: QueryFunctionContext,
): Promise<AccountTicketsOverviewData> => {
  const { allPageEvents } = await datoRequester(PageEventsCardsDocument, {
    locale,
    orderBy: 'startDatetime_DESC',
    first: 500,
    filter: {
      ticketsEventId: {
        in: accountEventsOverview.map((info) => info.id),
      },
      OR: [
        { ticketsTimeslotId: { isBlank: true } },
        {
          ticketsTimeslotId: {
            in: accountEventsOverview
              .map((info) => info.timeslots.map((t) => t.id))
              .flat(),
          },
        },
      ],
    },
  })(ctx);

  const enhancedEvents = allPageEvents.reduce<Array<EnhancedTicketsEventCard>>(
    (acc, event) => {
      const matchingCountInfo = accountEventsOverview.find(
        (info) => info.id === event.ticketsEventId,
      );
      if (!matchingCountInfo) return acc;

      if (!event.ticketsTimeslotId) {
        acc.push({
          ...event,
          ticketCount: matchingCountInfo.ticketCount,
        });
      } else {
        const matchingTimeslotCount = matchingCountInfo.timeslots.find(
          (info) => info.id === event.ticketsTimeslotId,
        );

        const ticketCount = matchingTimeslotCount?.ticketCount ?? 0;

        if (ticketCount > 0) {
          acc.push({
            ...event,
            ticketCount,
          });
        }
      }
      return acc;
    },
    [],
  );

  return {
    upcomingEvents: enhancedEvents.filter(isUpcomingEvent),
    pastEvents: enhancedEvents.filter(isPastEvent),
  };
};

const accountTicketsOverviewQuery = (variables: { locale: SiteLocale }) =>
  queryOptions<AccountTicketsOverviewData, RestApiError>({
    queryKey: [TICKETS_PREFIX, 'account-overview', variables],
    queryFn: async (ctx) => {
      const { data } = await restCall(() => getAccountEventsOverview());

      return enrichAccountTicketsOverviewWithDatoCards(
        data,
        variables.locale,
        ctx,
      );
    },
  });

export const useAccountTicketsOverview = (
  config: Partial<ReturnType<typeof accountTicketsOverviewQuery>> = {},
) => {
  const { isSignedIn } = useAuth();
  const { locale } = useLocale();
  const query = useQuery({
    ...accountTicketsOverviewQuery({ locale }),
    retry: false,
    staleTime: StaleTime.ONE_HOUR,
    ...config,
    enabled: !!isSignedIn && config.enabled,
  });

  return {
    pastEvents: query.data?.pastEvents ?? [],
    upcomingEvents: query.data?.upcomingEvents ?? [],
    status: query.status,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
};
